import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { DemoButton, SplitText, useReveal } from 'components';
import { formatMoney, formatNumber } from 'src/utils/format';
import { gsap, useGSAP } from 'src/utils/gsap';
import { prefersReducedMotion } from 'src/utils/motion';

import { usePage } from '../../pages';

import style from './Results.module.scss';

const STATS = [
  { value: 250, decimals: 0 },
  { value: 12.6, decimals: 1 },
  { value: 10, decimals: 0 }
];

const SAVINGS_RATE = 0.126;
const LOGO_V = 'M29.99 9.843h6.944l4.844 15.523L50.488.962h6.474L44.91 31.296h-6.474z';
const SPEND = { min: 5000, max: 200000, step: 5000, initial: 60000 };

// Rounded to tens, like the rest of the calculator
const roundMoney = (value, lang) => formatMoney(Math.round(value / 10) * 10, lang);

const Stat = ({ value, decimals, unit, text }) => {
  const { i18n } = useTranslation();
  const numberRef = useRef(null);
  const [current, setCurrent] = useState(prefersReducedMotion ? value : 0);

  useGSAP(() => {
    if (prefersReducedMotion) return;

    const counter = { value: 0 };
    gsap.to(counter, {
      value,
      duration: 2.4,
      ease: 'power3.out',
      scrollTrigger: { trigger: numberRef.current, start: 'top 88%', once: true },
      onUpdate: () => setCurrent(counter.value)
    });
  });

  return (
    <div
      className={style.stat}
      data-reveal
    >
      <p
        ref={numberRef}
        className={style.num}
      >
        <span>{formatNumber(current, i18n.language, decimals)}</span>
        <span className={style.unit}>{unit}</span>
      </p>
      <p>{text}</p>
    </div>
  );
};

const Results = () => {
  const { t, i18n } = useTranslation('page');
  const { id } = usePage();
  const sectionRef = useRef(null);
  const yearRef = useRef(SPEND.initial * SAVINGS_RATE * 12);
  const tweenRef = useRef(null);
  const [spend, setSpend] = useState(SPEND.initial);
  const [yearly, setYearly] = useState(yearRef.current);

  const stats = t(`${id}.results.stats`, { returnObjects: true });
  const lang = i18n.language;
  const fill = ((spend - SPEND.min) / (SPEND.max - SPEND.min)) * 100;

  useReveal(sectionRef);

  const { contextSafe } = useGSAP({ scope: sectionRef });

  const animateYearly = contextSafe((target) => {
    tweenRef.current?.kill();
    if (prefersReducedMotion) {
      yearRef.current = target;
      setYearly(target);
      return;
    }

    const counter = { value: yearRef.current };
    tweenRef.current = gsap.to(counter, {
      value: target,
      duration: 0.6,
      ease: 'power3.out',
      onUpdate: () => {
        yearRef.current = counter.value;
        setYearly(counter.value);
      }
    });
  });

  const handleSpendChange = (e) => {
    const value = Number(e.target.value);
    setSpend(value);
    animateYearly(value * SAVINGS_RATE * 12);
  };

  return (
    <section
      id='wyniki'
      ref={sectionRef}
      className='sec tone-dark'
      data-tone='dark'
    >
      <div
        className={style.bg}
        aria-hidden='true'
      >
        {/* The "v" from the logo as a violet outline with a soft glow */}
        <svg viewBox='28 -1 31 34'>
          <defs>
            <linearGradient
              id='results-v-stroke'
              gradientUnits='userSpaceOnUse'
              x1='56'
              y1='1'
              x2='32'
              y2='31'
            >
              <stop
                offset='0'
                stopColor='#C9C4FF'
              />
              <stop
                offset='.5'
                stopColor='#8B83F5'
              />
              <stop
                offset='1'
                stopColor='#645BE8'
              />
            </linearGradient>
            <filter
              id='results-v-glow'
              filterUnits='userSpaceOnUse'
              x='25'
              y='-4'
              width='37'
              height='40'
            >
              <feGaussianBlur stdDeviation='.35' />
            </filter>
          </defs>
          <path
            d={LOGO_V}
            fill='none'
            stroke='url(#results-v-stroke)'
            strokeWidth='8'
            strokeLinejoin='round'
            vectorEffect='non-scaling-stroke'
            filter='url(#results-v-glow)'
            opacity='.7'
          />
          <path
            d={LOGO_V}
            fill='none'
            stroke='url(#results-v-stroke)'
            strokeWidth='2'
            strokeLinejoin='round'
            vectorEffect='non-scaling-stroke'
          />
        </svg>
      </div>
      <div className='wrap'>
        <p className='mono-label'>{t(`${id}.results.label`)}</p>
        <SplitText
          as='h2'
          className={cn('h2', style.title)}
          reveal
        >
          {t(`${id}.results.title`)}
        </SplitText>

        <div className={style.stats}>
          {STATS.map((stat, i) => (
            <Stat
              key={i}
              {...stat}
              {...stats[i]}
            />
          ))}
        </div>

        <div
          className={style.calc}
          id='kalkulator'
          data-reveal
        >
          <div>
            <p className='mono-label'>{t(`${id}.results.calc.label`)}</p>
            <h3>{t(`${id}.results.calc.title`)}</h3>
            <label htmlFor='spend'>{t(`${id}.results.calc.spend`)}</label>
            <output htmlFor='spend'>{formatMoney(spend, lang)}</output>
            <input
              type='range'
              id='spend'
              min={SPEND.min}
              max={SPEND.max}
              step={SPEND.step}
              value={spend}
              onChange={handleSpendChange}
              style={{ '--p': `${fill}%` }}
            />
            <div
              className={style.scale}
              aria-hidden='true'
            >
              <span>{t(`${id}.results.calc.scaleMin`)}</span>
              <span>{t(`${id}.results.calc.scaleMax`)}</span>
            </div>
          </div>
          <div className={style.result}>
            <p className={cn('mono-label', style.resultLabel)}>{t(`${id}.results.calc.result`)}</p>
            <p className={style.big}>{roundMoney(yearly, lang)}</p>
            <p className={style.small}>
              {t(`${id}.results.calc.monthStart`)} <b>{roundMoney(spend * SAVINGS_RATE, lang)}</b>{' '}
              {t(`${id}.results.calc.monthEnd`)}
            </p>
            <DemoButton>{t(`${id}.results.calc.cta`)}</DemoButton>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Results;
